import type { z } from "zod"

// Все ключи приложения живут под одним префиксом, а скоуп в имени ключа
// определяет, что с ним происходит при clearSession:
//   luni:device:<feature>           — устройство, переживает логаут;
//   luni:user:<userId>:<feature>    — флаги конкретного пользователя,
//                                     переживают логаут (ключ уже разделён по id);
//   luni:session:<feature>          — удаляется при каждом clearSession,
//                                     на всех платформах, включая Telegram.
// Сторонние ключи (Paraglide, PostHog, Devtools, токены) остаются как есть.
const STORAGE_PREFIX = "luni"
const SESSION_KEY_PREFIX = `${STORAGE_PREFIX}:session:`

interface StorageItemOptions<T> {
	key: string
	schema: z.ZodType<T>
	// Возвращается, если ключа нет, хранилище недоступно или данные не прошли
	// схему (например, их записала старая OTA-версия в другом формате).
	fallback: T
	// Если само хранилище бросило ошибку. По умолчанию — fallback; отдельное
	// значение нужно там, где «не смогли прочитать» безопаснее трактовать
	// иначе, чем «ключа нет».
	onReadError?: T
}

interface StorageItem<T> {
	get: () => T
	// false — хранилище отказало в записи (квота, приватный режим).
	set: (value: T) => boolean
	update: (updater: (value: T) => T) => T
	remove: () => void
}

interface UserStorageItem<T> {
	get: (userId: number) => T
	set: (userId: number, value: T) => boolean
	update: (userId: number, updater: (value: T) => T) => T
	remove: (userId: number) => void
}

function bind<T>(
	fullKey: string,
	{ schema, fallback, onReadError = fallback }: StorageItemOptions<T>,
): StorageItem<T> {
	const read = (): T => {
		let raw: string | null
		try {
			raw = window.localStorage.getItem(fullKey)
		} catch {
			return onReadError
		}
		if (raw === null) return fallback
		try {
			const parsed = schema.safeParse(JSON.parse(raw))
			if (!parsed.success) return fallback
			return parsed.data
		} catch {
			return fallback
		}
	}
	const write = (value: T): boolean => {
		try {
			window.localStorage.setItem(fullKey, JSON.stringify(value))
			return true
		} catch {
			return false
		}
	}

	return {
		get: read,
		set: write,
		update: (updater) => {
			const next = updater(read())
			write(next)
			return next
		},
		remove: () => {
			try {
				window.localStorage.removeItem(fullKey)
			} catch {}
		},
	}
}

function scopedKey(scope: "device" | "session", key: string): string {
	return `${STORAGE_PREFIX}:${scope}:${key}`
}

const PERSISTENT_KEY_PATTERN = new RegExp(`^${STORAGE_PREFIX}:(?:device|user):`)

// device- и user-ключи переживают логаут, даже если модуль фичи не загружен.
export function survivesLogout(fullKey: string): boolean {
	return PERSISTENT_KEY_PATTERN.test(fullKey)
}

export function clearSessionScope(): void {
	try {
		for (const key of Object.keys(window.localStorage)) {
			if (key.startsWith(SESSION_KEY_PREFIX))
				window.localStorage.removeItem(key)
		}
	} catch {}
}

export function createDeviceStorageItem<T>(
	options: StorageItemOptions<T>,
): StorageItem<T> {
	return bind(scopedKey("device", options.key), options)
}

export function createSessionStorageItem<T>(
	options: StorageItemOptions<T>,
): StorageItem<T> {
	return bind(scopedKey("session", options.key), options)
}

export function createUserStorageItem<T>(
	options: StorageItemOptions<T>,
): UserStorageItem<T> {
	const items = new Map<number, StorageItem<T>>()
	const itemFor = (userId: number) => {
		let item = items.get(userId)
		if (!item) {
			item = bind(`${STORAGE_PREFIX}:user:${userId}:${options.key}`, options)
			items.set(userId, item)
		}
		return item
	}

	return {
		get: (userId) => itemFor(userId).get(),
		set: (userId, value) => itemFor(userId).set(value),
		update: (userId, updater) => itemFor(userId).update(updater),
		remove: (userId) => itemFor(userId).remove(),
	}
}

const KEY_PATTERN = new RegExp(
	`^${STORAGE_PREFIX}:(?:(device|session):(.+)|user:\\d+:(.+))$`,
)

// Имя фичи из ключа нашей схемы; для чужих ключей — null.
export function storageFeatureOf(fullKey: string): string | null {
	const match = fullKey.match(KEY_PATTERN)
	if (!match) return null
	return match[2] ?? match[3] ?? null
}
